import React from "react";
import { Link } from "react-router-dom";
import { lastUpdated, version } from "../data/AppData";
const TermsPage = () => {
  const sections = [
    {
      id: "acceptance",
      title: "1. Acceptance of Agreement",
      content: `By creating a ContactHQ account, or by accessing our "No-Backend" form services, you agree to be bound by these Terms. This Agreement constitutes the entire and only agreement between us and you. We provide our services to a diverse range of developers and businesses, specifically focusing on markets in West Bengal, Rajasthan, Delhi, and globally. If you do not agree to these terms, you must cease use of the platform immediately.`,
    },
    {
      id: "service-scope",
      title: "2. Scope of Service",
      content: `ContactHQ provides a smart form generator, a centralized inbox, and secure form endpoints. 
      (a) Smart Form Builder: You can create, customize, publish, and embed forms using our visual form-building tools and templates. 
      (b) Availability: While we strive for 99.9% uptime, we do not guarantee uninterrupted service. 
      (c) Modification: We reserve the right to update features (e.g., Centralized Inbox logic) to improve performance for our Indian and global user base.`,
    },
    {
      id: "account-security",
      title: "3. Account Responsibility",
      content: `To access the dashboard, you must register using valid credentials. You are solely responsible for:
      (a) Maintaining the secrecy of your password.
      (b) All activities that occur under your account.
      (c) Ensuring your contact information (Email/Phone) is up to date for security notifications.
      ContactHQ will not be liable for any loss or damage arising from your failure to comply with this security obligation.`,
    },
    {
      id: "user-content",
      title: "4. Data & Form Submissions",
      content: `(a) Ownership: You retain all rights to the data submitted through your forms. 
      (b) License: You grant ContactHQ a worldwide, non-exclusive license to host, store, and transmit your data solely for the purpose of providing the service (e.g., displaying messages in your Inbox).
      (c) Prohibited Content: You may not use our forms to collect highly sensitive data (like Aadhaar numbers or clear-text passwords) without secondary encryption methods. You are responsible for ensuring your forms comply with the Digital Personal Data Protection Act (DPDP) of India.`,
    },
    {
      id: "payment-terms",
      title: "5. Billing, Plans & Taxes",
      content: `(a) Pricing: Prices for Silver, Gold, and Diamond plans are as shown on our Pricing page. 
      (b) Currency: For users in India, billing is processed in INR (₹). Global users are billed in USD ($).
      (c) Taxes: You are responsible for all applicable taxes, including GST in India. 
      (d) Cancellations: You may cancel your subscription at any time; however, no refunds are provided for partial months of service.`,
    },
    {
      id: "compliance",
      title: "6. Regional Compliance (India)",
      content: `ContactHQ is optimized for the Indian regulatory environment. We acknowledge the importance of data residency and processing standards in West Bengal, Rajasthan, and Delhi. We handle all data in accordance with prevailing Indian IT laws and are committed to transparency regarding our secure form endpoints.`,
    },
    {
      id: "termination",
      title: "7. Termination of Use",
      content: `We may terminate or suspend your access to the service immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the service will cease immediately. If you wish to terminate your account, you may simply discontinue using the service or delete your account from the settings.`,
    },
    {
      id: "governing-law",
      title: "8. Governing Law & Jurisdiction",
      content: `These Terms shall be governed and construed in accordance with the laws of India. Any legal action or proceeding related to your access to, or use of, the Service shall be instituted in a state or federal court in Kolkata, West Bengal. You agree to submit to the jurisdiction of, and agree that venue is proper in, these courts.`,
    },
  ];

  return (
    <div className="bg-white min-h-screen selection:bg-[#E63946] selection:text-white font-sans overflow-x-hidden">
      {/* 1. HERO SECTION - Synced with BugsPage Theme */}
      <section className="relative pt-28 sm:pt-32 pb-12 sm:pb-16 lg:pb-20 bg-[#0B1C2D] overflow-hidden">
        {/* Subtle Background Red Dot Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(#E63946 0.5px, transparent 0.5px)",
              backgroundSize: "30px 30px",
            }}
          ></div>
        </div>

        <div className="mx-auto w-[95%] md:w-[90%] relative z-10">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-bold text-[#E63946] mb-8 group animate-fade-in-up"
          >
            <span className="material-symbols-outlined text-sm mr-2 group-hover:-translate-x-1 transition-transform">
              arrow_back
            </span>
            Return to Homepage
          </Link>
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-none mb-4 sm:mb-6 animate-fade-in-up">
            Terms of <span className="text-[#E63946]">Service.</span>
          </h1>
          <p className="max-w-2xl text-base sm:text-lg md:text-xl text-slate-400 font-medium leading-relaxed animate-fade-in-up">
            Everything you need to know about your legal rights,
            responsibilities, and how we manage the ContactHQ platform for our
            global community.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-in-up">
            <span className="px-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-[11px] font-black text-white uppercase tracking-widest">
              Version {version}
            </span>
            <span className="text-sm font-bold text-slate-500">
              Last updated: {lastUpdated}
            </span>
          </div>
        </div>
      </section>

      {/* 2. CONTENT GRID */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="mx-auto w-[95%] md:w-[90%]">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* LEFT: SIDEBAR NAV */}
            <aside className="lg:w-1/4">
              <div className="lg:sticky lg:top-32 overflow-x-auto">
                <nav className="space-y-1">
                  {sections.map((sec) => (
                    <a
                      key={sec.id}
                      href={`#${sec.id}`}
                      className="flex items-center justify-between py-3 px-4 rounded-xl text-sm font-bold text-[#64748B] hover:text-[#E63946] hover:bg-red-50 transition-all group"
                    >
                      {sec.title.split(".")[1]}
                      <span className="material-symbols-outlined text-xs opacity-0 group-hover:opacity-100 transition-all">
                        chevron_right
                      </span>
                    </a>
                  ))}
                </nav>
                <div className="mt-8 p-6 rounded-2xl bg-slate-50 border border-[#E2E8F0]">
                  <p className="text-[10px] font-black text-[#0B1C2D] uppercase tracking-widest mb-2">
                    Legal Compliance
                  </p>
                  <p className="text-xs text-[#64748B] font-medium leading-relaxed">
                    Optimized for DPDP Act (India) and global data processing
                    standards.
                  </p>
                </div>
              </div>
            </aside>

            {/* RIGHT: DETAILED CONTENT */}
            <main className="lg:w-3/4">
              <div className="space-y-12 sm:space-y-16 lg:space-y-20">
                {sections.map((sec) => (
                  <article
                    key={sec.id}
                    id={sec.id}
                    className="scroll-mt-32 animate-fade-in-up"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-[#E63946] font-black text-xl">
                        /
                      </span>
                      <h2 className="text-2xl md:text-3xl font-black text-[#0B1C2D] tracking-tight">
                        {sec.title}
                      </h2>
                    </div>
                    <div className="prose prose-slate max-w-none">
                      <p className="text-[#64748B] text-base sm:text-lg leading-7 sm:leading-9 whitespace-pre-line font-medium">
                        {sec.content}
                      </p>
                    </div>
                  </article>
                ))}
              </div>

              {/* FINAL CONTACT BLOCK */}
              <div className="mt-12 sm:mt-16 lg:mt-24 p-6 sm:p-8 lg:p-12 rounded-2xl sm:rounded-[3rem] bg-[#0B1C2D] text-white relative overflow-hidden group">
                <div className="relative z-10">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-black mb-3 sm:mb-4">
                    Questions about these Terms?
                  </h3>
                  <p className="text-slate-400 max-w-xl mb-8 leading-relaxed font-medium">
                    Our legal department is available to clarify any points
                    regarding data residency in India or our global service
                    agreements.
                  </p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-6">
                    <a
                      href="mailto:legal@contacthq.in"
                      className="flex items-center gap-3 bg-[#E63946] px-6 py-3 rounded-xl font-black hover:bg-[#be123c] transition-all shadow-lg shadow-red-900/20"
                    >
                      <span className="material-symbols-outlined">mail</span>
                      legal@contacthq.in
                    </a>
                    <div className="flex items-center gap-3 text-slate-300 font-bold px-6 py-3">
                      <span className="material-symbols-outlined text-[#E63946]">
                        location_on
                      </span>
                      Kolkata Office
                    </div>
                  </div>
                </div>
                {/* Decoration */}
                <div className="absolute top-0 right-0 size-64 bg-white/5 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-[#E63946]/10 transition-all duration-700"></div>
              </div>
            </main>
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default TermsPage;
